import { useState, useEffect, useRef } from 'react';
import AceIdentity from '@ace-clublabs/ace-identity';
import { OktaAuth } from '@okta/okta-auth-js';
import { useLocation } from 'react-router-dom';
import { Pages } from '../utils/constants';

/**
 * Custom hook to manage session state and expiration
 * @returns Function to set the session expiration and onSessionExpire handler
 */
export function useSessionTimeout() {
  const location = useLocation();
  const timeoutIDRef = useRef<NodeJS.Timeout | null>(null);
  const [sessionExpirationTime, setSessionExpirationTime] = useState<number | undefined>(undefined);
  const [sessionExpirationHandler, setSessionExpirationHandler] = useState<(() => void) | undefined>(undefined);

  // This useEffect handles creation of the session timeout only after a session expiration time has been set
  // It also has a dependency on location to refresh the timeout when the user navigates to a different page
  useEffect(() => {
    // Don't set the expiration timer if we're on an error page or session parameters are not set
    const isErrorPage = [
      Pages.TimeoutError,
      Pages.SorryPage,
      Pages.PaymentError,
    ].includes(location.pathname as Pages);

    if (!sessionExpirationTime || !sessionExpirationHandler || isErrorPage) {
      return;
    }

    const currentTime = getCurrentEpochTime();

    // Set the remaining session duration in seconds, ensuring it's not negative
    const remainingSessionDuration = Math.max(sessionExpirationTime - currentTime, 0);

    timeoutIDRef.current = setTimeout(() => {
      sessionExpirationHandler();
    }, remainingSessionDuration * 1000);

    return () => {
      if (timeoutIDRef.current) {
        clearTimeout(timeoutIDRef.current);
      }
    };
  }, [sessionExpirationTime, sessionExpirationHandler, location]);

  function setSessionTimeoutParameters(sessionParameters: SessionTimeoutParameters) {
    const { onSessionExpired, expiresAt } = sessionParameters;
    setSessionExpirationTime(expiresAt);
    setSessionExpirationHandler(() => onSessionExpired);
  }

  return { setSessionTimeoutParameters };
}

/**
 * SessionParameters
 * @param onSessionExpired Callback function to be called when the session expires
 * @param expiresAt Epoch time in seconds when the session will expire
 */
export type SessionTimeoutParameters = {
  onSessionExpired: () => void;
  expiresAt: number;
};

export function getCurrentEpochTime() {
  return Math.floor(Date.now() / 1000);
}

// Currently only the base path and /sorry/timeout are supported post logout redirect urls in lower env
export function getPostLogoutRedirectUrl(page?: Pages.TimeoutError) {
  return `https://${window.location.host}/membership/selfservice${page || ''}`;
}

/**
 * Default session duration for MSS sessions in seconds, 120 minutes consistent with ACE Identity default session duration
 */
export const MSS_DEFAULT_SESSION_DURATION = 120 * 60; // 120 minutes in seconds

/**
 * MSSSession class to manage MSS session expiration
 * @param aceIdentity AceIdentity instance needed to retrieve tokens, as a fallback if sessionStorage tokens can't be found.
 * @param sessionDuration Session duration in seconds
 */
export class MSSSession {
  sessionExpirationTime: number | undefined;
  constructor(public aceIdentity: AceIdentity, public sessionDuration: number = MSS_DEFAULT_SESSION_DURATION) {}

  /**
   * Gets the session expiration time, defauts to current time if the id token is not found
   * @returns Promise that resolves to the session expiration time in epoch seconds
   */
  async getSessionExpirationTime() {
    const currentTime = this.getCurrentEpochTime();
    try {
      const authTime = (await this.getAuthTime()) ?? currentTime;
      const sessionExpirationTime = authTime + this.sessionDuration;
      this.sessionExpirationTime = sessionExpirationTime;
    } catch (error) {
      this.sessionExpirationTime = currentTime + this.sessionDuration;
    }
    return this.sessionExpirationTime;
  }

  /**
   * Attempts to get the auth_time claim from the Okta ID token either through sessionStorage or AceIdentity
   * @returns Auth time in epoch time in seconds, or undefined if it can't be found
   */
  async getAuthTime(): Promise<number | undefined> {
    try {
      let idToken;
      const oktaTokenStorage = JSON.parse(sessionStorage.getItem('okta-token-storage') || '{}');
      if (!oktaTokenStorage.idToken) {
        const oktaAuth = this.aceIdentity.oktaAuth() as OktaAuth;
        const tokenResponse = await oktaAuth.token.getWithoutPrompt({
          scopes: ['profile', 'clubCode', 'scope', 'openid', 'customerId', 'membershipStatus'],
        });
        const tokens = tokenResponse?.tokens;
        idToken = tokens?.idToken;
      } else if (oktaTokenStorage?.idToken) {
        idToken = oktaTokenStorage.idToken;
      }

      if (!idToken?.claims || !(idToken?.claims as any)?.auth_time) {
        return undefined;
      }

      const authTime = (idToken?.claims as any)?.auth_time;
      return authTime as number;
    } catch (error) {
      return undefined;
    }
  }

  getCurrentEpochTime() {
    return Math.floor(Date.now() / 1000);
  }
}
